import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, MenuToggle, MenuOverlay } from "../layouts";
import {
  MapInstanceContext,
  WallLayer,
  LeafletOverrides,
  MapWrap,
  MapToggleButton as ToggleBtn,
  makeDungeonIconHtml,
  makeDungeonIconFarHtml,
  makePlayerIconHtml,
  makeAddressIconHtml,
  buildDungeonPopup,
  buildGlobalDungeonPopup,
  buildAddressPopup,
  StatusLine,
  MapControls,
} from "components/Map";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useTheme } from "styled-components";
import { Chiclet, Icon } from "components";
import { useGeolocation, useDebounce, useToast, useLocalStorage } from "hooks";
import { useFeatureFlag, useStateValue } from "providers";
import { apiFetch } from "actions/apiFetch";
import { getAdminDungeons } from "actions/api";
import {
  CharacterSelect,
  getActiveSave,
} from "../games/pinmonkey/components/character-select";
import { CharacterPanel } from "../games/pinmonkey/components/CharacterPanel";
import { WallHUD } from "../games/wall/wall-hud";
import { WallTour } from "./components/WallTour";

export const Home = () => {
  const [{ session, global: { theme = "default", variant } = {} }] = useStateValue();
  const isAdmin = session?.user?.roles?.some((r) => r.slug === "admin");
  const isLoggedIn = !!session?.user;
  const { DungeonGame, isLoading: flagsLoading } = useFeatureFlag();
  const {
    data: geo,
    loading: geoLoading,
    source: geoSource,
  } = useGeolocation();
  const debouncedLat = useDebounce(geo?.latitude, 1500);
  const debouncedLng = useDebounce(geo?.longitude, 1500);
  const navigate = useNavigate();
  const { colors, icons } = useTheme();
  const resolvedVariant = variant ?? 0;
  const tileUrl = `https://{s}.basemaps.cartocdn.com/${resolvedVariant === 0 ? "dark_all" : "light_all"}/{z}/{x}/{y}{r}.png?key=${process.env.CARTO_API_KEY}`;
  const mapOverlayColor = theme === "fall" ? colors("link", 0.18) : null;
  const mapOverlayBlendMode = resolvedVariant === 0 ? "multiply" : "soft-light";
  const isLoggedInRef = useRef(isLoggedIn);
  useEffect(() => {
    isLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn]);

  const mapDivRef = useRef(null);
  const mapRef = useRef(null);

  const [dungeons, setDungeons] = useState([]);
  const [globalDungeon, setGlobalDungeon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  const [menuOpen, setMenuOpen] = useState(false);
  // Parsed once on mount and split into 3 independently-toggleable booleans
  // (each has its own setter used throughout this component) — the combined
  // write-back lives in the effect below.
  const [savedToggles] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("map:toggles")) || {};
    } catch {
      return {};
    }
  });
  const [showDungeons, setShowDungeons] = useState(!!savedToggles.dungeons);
  const [showAddresses, setShowAddresses] = useState(!!savedToggles.addresses);
  const [showWall, setShowWall] = useState(!!savedToggles.wall);
  const [mapReady, setMapReady] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [mapCenter, setMapCenter] = useState(null);
  const debouncedCenterLat = useDebounce(mapCenter?.lat, 1500);
  const debouncedCenterLng = useDebounce(mapCenter?.lng, 1500);
  const addressMarkersRef = useRef([]);
  const dungeonMarkersRef = useRef([]);
  const globalMarkerRef = useRef(null);
  const mapInitializedRef = useRef(false);
  const geoRef = useRef(geo);
  useEffect(() => {
    geoRef.current = geo;
  }, [geo]);

  const [activeCharacter, setActiveCharacter] = useState(() => getActiveSave());
  const activeCharacterRef = useRef(activeCharacter);
  useEffect(() => {
    activeCharacterRef.current = activeCharacter;
  }, [activeCharacter]);
  const [charSelectOpen, setCharSelectOpen] = useState(false);
  const [pendingDungeon, setPendingDungeon] = useState(null);

  // Drawing state
  const [pendingPixels, setPendingPixels] = useLocalStorage('wall:pendingPixels', []);
  const [activeColor, setActiveColor] = useState("#2a2a2a");
  const [wallKey, setWallKey] = useState(0);
  const { showToast: showWallToast, toastElement: wallToastElement } = useToast();
  const [isPicking, setIsPicking] = useState(false);
  const [pickedColor, setPickedColor] = useState(null);
  const [isErasing, setIsErasing] = useState(false);
  const activeColorRef = useRef(activeColor);
  useEffect(() => {
    activeColorRef.current = activeColor;
  }, [activeColor]);

  useEffect(() => {
    localStorage.setItem(
      "map:toggles",
      JSON.stringify({
        dungeons: showDungeons,
        addresses: showAddresses,
        wall: showWall,
      })
    );
  }, [showDungeons, showAddresses, showWall]);

  // Capture geo at first draw so save uses the same position, not a later GPS update
  const drawingPositionRef = useRef(null);
  const [drawingPosition, setDrawingPosition] = useState(null);

  const pendingByTile = useMemo(() => {
    const grouped = {};
    pendingPixels.forEach(({ z, x, y, cx, cy, color }) => {
      const key = `${z}/${x}/${y}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push({ x: cx, y: cy, color });
    });
    return grouped;
  }, [pendingPixels]);

  const handleCellDraw = useCallback(({ z, x, y, cx, cy }) => {
    if (!drawingPositionRef.current) {
      drawingPositionRef.current = geoRef.current;
      setDrawingPosition(geoRef.current);
    }
    setPendingPixels((prev) => {
      const filtered = prev.filter(
        (p) =>
          !(p.z === z && p.x === x && p.y === y && p.cx === cx && p.cy === cy)
      );
      filtered.push({ z, x, y, cx, cy, color: activeColorRef.current });
      return filtered;
    });
  }, []);

  const handlePickColor = useCallback((color) => {
    setIsPicking(false);
    if (color) setPickedColor(color);
  }, []);

  // Removes a pending (unsaved) pixel at this cell, if any — already-saved
  // pixels aren't in pendingPixels so this can never touch committed drawings.
  const handleEraseCell = useCallback(({ z, x, y, cx, cy }) => {
    setPendingPixels((prev) =>
      prev.filter((p) => !(p.z === z && p.x === x && p.y === y && p.cx === cx && p.cy === cy)),
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartPick = useCallback(() => {
    setIsPicking(true);
    setIsErasing(false);
  }, []);

  const handleToggleErase = useCallback(() => {
    setIsErasing((prev) => {
      const next = !prev;
      if (next) setIsPicking(false);
      return next;
    });
  }, []);

  const handleSaved = useCallback((lockedCells) => {
    setPendingPixels([]);
    setWallKey((k) => k + 1);
    drawingPositionRef.current = null;
    setDrawingPosition(null);
    if (lockedCells?.size) {
      showWallToast(
        lockedCells.size === 1
          ? "1 pixel couldn't be saved — already drawn in the last 24h"
          : `${lockedCells.size} pixels couldn't be saved — already drawn in the last 24h`
      );
    }
  }, [showWallToast]);

  // Create Leaflet map once
  useEffect(() => {
    if (!mapDivRef.current || mapRef.current) return;
    const SAVED_VIEW_MAX_AGE = 30 * 60 * 1000; // ignore stale saved position — geolocation should win
    const saved = (() => {
      try {
        const parsed = JSON.parse(localStorage.getItem("map:view"));
        if (parsed?.ts && Date.now() - parsed.ts > SAVED_VIEW_MAX_AGE) return null;
        return parsed;
      } catch {
        return null;
      }
    })();
    const initCenter = saved ? [saved.lat, saved.lng] : [0, 0];
    const initZoom = saved?.zoom ?? 18;
    // Note: a saved view only seeds the initial camera (avoids a flash to
    // [0,0] while geolocation resolves) — it must NOT mark the map as
    // already-initialized. Doing so used to skip the pan-to-geo effect below
    // entirely, leaving the map parked wherever it was last scrolled to
    // (possibly nowhere near the user) while the player marker and the wall
    // game's range check both still used the real, current geolocation —
    // so a user could look at a stale area, try to draw, and get rejected
    // as "too far away" from a position they weren't even looking at.

    const map = L.map(mapDivRef.current, {
      zoom: initZoom,
      center: initCenter,
      zoomControl: true,
      attributionControl: true,
    });

    const saveView = () => {
      const c = map.getCenter();
      localStorage.setItem(
        "map:view",
        JSON.stringify({ lat: c.lat, lng: c.lng, zoom: map.getZoom(), ts: Date.now() })
      );
    };
    const handleViewChange = () => {
      saveView();
      const c = map.getCenter();
      setMapCenter({ lat: c.lat, lng: c.lng });
    };
    map.on("moveend", handleViewChange);
    map.on("zoomend", handleViewChange);
    setMapCenter({ lat: initCenter[0], lng: initCenter[1] });

    const LocateControl = L.Control.extend({
      onAdd() {
        const bar = L.DomUtil.create("div", "leaflet-bar leaflet-control");
        const a = L.DomUtil.create("a", "", bar);
        a.href = "#";
        a.title = "Center on my location";
        a.setAttribute("role", "button");
        a.style.cssText =
          "display:flex;align-items:center;justify-content:center;";
        a.innerHTML =
          `<svg width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="${icons['crosshairs-gps']}"/></svg>`;
        L.DomEvent.on(a, "click", (e) => {
          L.DomEvent.stop(e);
          const pos = geoRef.current;
          if (pos?.latitude) map.panTo([pos.latitude, pos.longitude]);
        });
        return bar;
      },
    });
    new LocateControl({ position: "topleft" }).addTo(map);

    mapRef.current = map;
    setMapReady(true);
    return () => {
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, []);

  // Swap tile layer when the theme (dark/light, or collection) changes
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const layer = L.tileLayer(tileUrl, {
      subdomains: "abcd",
      maxZoom: 19,
      attribution: "© OpenStreetMap contributors © CARTO",
    });
    layer.addTo(mapRef.current);
    return () => { mapRef.current?.removeLayer(layer); };
  }, [mapReady, tileUrl]);

  const playerMarkerRef = useRef(null);

  // Pan to the user's real location once per page load (even over a saved
  // view — see note above) and place player marker
  useEffect(() => {
    if (!geo?.latitude || !geo?.longitude || !mapRef.current) return;
    if (geoSource === "loading") return;
    const isGps = geoSource === "gps" || geoSource === "fallback";
    if (!mapInitializedRef.current) {
      if (isGps) mapInitializedRef.current = true;
      mapRef.current.setView([geo.latitude, geo.longitude], 18);
    }
    if (!playerMarkerRef.current) {
      playerMarkerRef.current = L.marker([geo.latitude, geo.longitude], {
        icon: L.divIcon({
          className: "",
          html: makePlayerIconHtml(colors),
          iconSize: [14, 14],
          iconAnchor: [7, 7],
        }),
        zIndexOffset: -100,
      }).addTo(mapRef.current);
    } else {
      playerMarkerRef.current.setLatLng([geo.latitude, geo.longitude]);
    }
  }, [geo?.latitude, geo?.longitude, geoSource]);

  // Fetch dungeons when dungeon toggle is on
  useEffect(() => {
    if (!showDungeons) {
      setDungeons([]);
      setGlobalDungeon(null);
      return;
    }
    if (geoSource === "loading" || !debouncedLat || !debouncedLng) return;
    setLoading(true);
    setStatus("Searching for dungeons…");

    const nearbyFetch = apiFetch({
      path: `/dungeons/nearby?lat=${debouncedLat}&lng=${debouncedLng}`,
    });
    const dungeonFetch = isAdmin
      ? Promise.all([
          getAdminDungeons().then((data) =>
            (data?.dungeons?.rows ?? [])
              .filter((d) => !d.is_global)
              .map((d) => ({ ...d, distance: 0 }))
          ),
          nearbyFetch.then((data) => data?.global ?? null),
        ]).then(([dungeons, global]) => ({ dungeons, global }))
      : nearbyFetch.then((data) => ({
          dungeons: data?.dungeons ?? [],
          global: data?.global ?? null,
        }));

    dungeonFetch
      .then((result) => {
        setDungeons(result.dungeons);
        setGlobalDungeon(result.global);
        setLoading(false);
        setStatus("");
      })
      .catch(() => {
        setStatus("Could not load dungeons.");
        setLoading(false);
      });
  }, [isAdmin, geoSource, debouncedLat, debouncedLng, showDungeons]);

  // Place dungeon markers
  useEffect(() => {
    dungeonMarkersRef.current.forEach((m) => m.remove());
    dungeonMarkersRef.current = [];
    if (!mapRef.current || !showDungeons || dungeons.length === 0) return;

    const iconNear = L.divIcon({
      className: "",
      html: makeDungeonIconHtml(colors),
      iconSize: [16, 16],
      iconAnchor: [8, 8],
      popupAnchor: [0, -12],
    });
    const iconFar = L.divIcon({
      className: "",
      html: makeDungeonIconFarHtml(colors),
      iconSize: [16, 16],
      iconAnchor: [8, 8],
      popupAnchor: [0, -12],
    });

    const nav = {
      navigate,
      isLoggedInRef,
      activeCharacterRef,
      onNeedCharacter: (d) => {
        setPendingDungeon(d);
        setCharSelectOpen(true);
      },
    };

    dungeons.forEach((d) => {
      if (d.lat == null || d.lng == null) return;
      const inRange =
        d.radius == null || (d.distance != null && d.distance <= d.radius);

      const marker = L.marker([d.lat, d.lng], {
        icon: inRange ? iconNear : iconFar,
        zIndexOffset: inRange ? 500 : 400,
      })
        .addTo(mapRef.current)
        .bindPopup(buildDungeonPopup(colors, d, nav), {
          className: "dungeon-popup",
          maxWidth: 220,
        });
      dungeonMarkersRef.current.push(marker);
    });

    return () => {
      dungeonMarkersRef.current.forEach((m) => m.remove());
      dungeonMarkersRef.current = [];
    };
  }, [dungeons, showDungeons]);

  // Global dungeon marker
  useEffect(() => {
    if (globalMarkerRef.current) {
      globalMarkerRef.current.remove();
      globalMarkerRef.current = null;
    }
    const currentGeo = geoRef.current;
    if (
      !mapRef.current ||
      !showDungeons ||
      !globalDungeon ||
      geoLoading ||
      !currentGeo?.latitude ||
      !currentGeo?.longitude
    )
      return;

    const YARDS_200_DEG = 183 / 111320;
    const markerLat = currentGeo.latitude + YARDS_200_DEG;
    const markerLng = currentGeo.longitude;

    const icon = L.divIcon({
      className: "",
      html: makeDungeonIconHtml(colors),
      iconSize: [16, 16],
      iconAnchor: [8, 8],
      popupAnchor: [0, -12],
    });

    const popup = buildGlobalDungeonPopup(colors, globalDungeon, {
      navigate,
      isLoggedInRef,
      activeCharacterRef,
      onNeedCharacter: (d) => {
        setPendingDungeon(d);
        setCharSelectOpen(true);
      },
    });

    globalMarkerRef.current = L.marker([markerLat, markerLng], {
      icon,
      zIndexOffset: 600,
    })
      .addTo(mapRef.current)
      .bindPopup(popup, { className: "dungeon-popup", maxWidth: 220 });

    return () => {
      if (globalMarkerRef.current) {
        globalMarkerRef.current.remove();
        globalMarkerRef.current = null;
      }
    };
  }, [globalDungeon, showDungeons, geoLoading]);

  // Fetch addresses — keyed off the map center so panning while Addresses
  // is active re-queries around wherever the user is looking, not their GPS fix.
  useEffect(() => {
    if (!showAddresses || !debouncedCenterLat || !debouncedCenterLng) return;
    apiFetch({
      path: `/places?lat=${debouncedCenterLat}&lng=${debouncedCenterLng}&limit=50`,
    })
      .then((data) => setAddresses(data?.addresses?.rows ?? []))
      .catch(() => {});
  }, [showAddresses, debouncedCenterLat, debouncedCenterLng]);

  // Place or remove address markers
  useEffect(() => {
    if (!mapRef.current) return;
    addressMarkersRef.current.forEach((m) => m.remove());
    addressMarkersRef.current = [];
    if (!showAddresses) return;

    const icon = L.divIcon({
      className: "",
      html: makeAddressIconHtml(colors),
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });

    addresses.forEach((a) => {
      const lat = parseFloat(a.latitude);
      const lng = parseFloat(a.longitude);
      if (!lat || !lng) return;

      const popup = buildAddressPopup(colors, a, {
        navigate,
        showClaim: !isLoggedIn,
        onClaim: (addr) =>
          navigate("/beta/host", {
            state: { location_id: addr.Location?.id, name: addr.Location?.name },
          }),
      });

      const marker = L.marker([lat, lng], { icon })
        .bindPopup(popup, { className: "dungeon-popup", maxWidth: 200 })
        .addTo(mapRef.current);
      addressMarkersRef.current.push(marker);
    });
  }, [showAddresses, addresses, isLoggedIn]);

  const showStatus = geoLoading ? "Locating you…" : status;

  return (
    <MapWrap>
      <LeafletOverrides />
      <div ref={mapDivRef} style={{ width: "100%", height: "100%" }} />
      {mapOverlayColor && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: mapOverlayColor,
            mixBlendMode: mapOverlayBlendMode,
            pointerEvents: "none",
            zIndex: 400,
          }}
        />
      )}

      {showStatus && <StatusLine>{showStatus}</StatusLine>}

      <MapControls>
        {DungeonGame?.variant === "hide" ? null : (
          <ToggleBtn
            $active={showDungeons}
            onClick={() => {
              setShowDungeons((v) => !v);
              setShowWall(false);
            }}
          >
            ⚔ Dungeons
          </ToggleBtn>
        )}
        <ToggleBtn
          $active={showAddresses}
          onClick={() => setShowAddresses((v) => !v)}
        >
          ⌂ Addresses
        </ToggleBtn>
        <ToggleBtn
          data-tour="drawing-toggle"
          $active={showWall}
          onClick={() => {
            setShowWall((v) => !v);
            setShowDungeons(false);
          }}
        >
          ✏ Drawing
        </ToggleBtn>
      </MapControls>

      {!flagsLoading && <WallTour />}

      {mapReady && (
        <MapInstanceContext.Provider value={mapRef.current}>
          <WallLayer
            active={showWall}
            pendingByTile={pendingByTile}
            onCellDraw={handleCellDraw}
            refreshKey={wallKey}
            userPosition={geo}
            isPicking={isPicking}
            onPickColor={handlePickColor}
            isErasing={isErasing}
            onEraseCell={handleEraseCell}
            onOutOfRange={() => showWallToast('Too far away to draw here — move closer')}
          />
        </MapInstanceContext.Provider>
      )}

      {showWall && (
        <WallHUD
          pendingPixels={pendingPixels}
          onColorChange={(c) => {
            setActiveColor(c);
            activeColorRef.current = c;
            setIsErasing(false);
          }}
          onSaved={handleSaved}
          onError={showWallToast}
          userPosition={drawingPosition}
          isPicking={isPicking}
          onStartPick={handleStartPick}
          pickedColor={pickedColor}
          isErasing={isErasing}
          onToggleErase={handleToggleErase}
        />
      )}

      {wallToastElement}

      {showDungeons && (
        <CharacterPanel
          isLoggedIn={isLoggedIn}
          activeCharacter={activeCharacter}
          onLogin={() => navigate("/login")}
          onChangeCharacter={() => setCharSelectOpen(true)}
          onSelectCharacter={() => setCharSelectOpen(true)}
        />
      )}

      {showDungeons && isLoggedIn && charSelectOpen && (
        <CharacterSelect
          onEnter={(save) => {
            setActiveCharacter(save);
            setCharSelectOpen(false);
            if (pendingDungeon) {
              navigate(`/games/dungeon/${pendingDungeon.slug}`);
              setPendingDungeon(null);
            }
          }}
          onCancel={() => {
            setCharSelectOpen(false);
            setPendingDungeon(null);
          }}
        />
      )}

      <MenuToggle onClick={() => setMenuOpen((v) => !v)}>
        <Chiclet>
          <Icon icon="menu" fill={colors("foreground")} />
        </Chiclet>
        <Menu show={menuOpen} close={() => setMenuOpen(false)} />
      </MenuToggle>
      <MenuOverlay $show={menuOpen} onClick={() => setMenuOpen(false)} />
    </MapWrap>
  );
};
